/**
 * Break policy — berekent pauze-minuten op basis van shift_type + shiftduur.
 * Bron: plans/archive/2026-04-21-auto-pauze-policy.md.
 *
 * ═══ BRON VAN WAARHEID ═══
 * Dit bestand is de canonieke pauze-policy. Verwacht (rooster) en werkelijk
 * (klok) gebruiken exact dezelfde regels. Er bestaan byte-identieke spiegels
 * (Vercel-functions kunnen niet buiten hun app-root importeren, Vite-apps
 * geen workspace-deps). Wijzig je iets, wijzig dan ALLE drie en draai
 * scripts/smoke-workforce.sh (hash-pariteitscheck):
 *   1. brands/cinco/apps/office-app/api/_lib/break-policy.ts   (dit bestand)
 *   2. brands/cinco/apps/office-app/src/lib/break-policy.ts
 *   3. apps/team-app/api/_lib/break-policy.ts
 *
 * Gebruik:
 *   const { type, source } = resolveShiftType({ scheduleShiftType, staffTeam })
 *   const { breakMinutes, paidBreakMinutes } = calculateBreak(type, grossHours)
 */

export type ShiftType = 'wasserij' | 'route' | 'kantoor' | 'algemeen'
export type BreakSource = 'schedule' | 'team_fallback' | 'manual' | 'clocked'

export interface BreakResult {
  breakMinutes: number
  paidBreakMinutes: number
}

export function calculateBreak(shiftType: ShiftType, grossHours: number): BreakResult {
  if (shiftType === 'wasserij') {
    // ≥7u: koffie 15 + lunch 30 + koffie 15 = 60
    if (grossHours >= 7) return { breakMinutes: 60, paidBreakMinutes: 0 }
    // 5.5–7u: koffie 15 + lunch 30 = 45
    if (grossHours >= 5.5) return { breakMinutes: 45, paidBreakMinutes: 0 }
    // 2.5–5.5u: 1 koffiepauze
    if (grossHours >= 2.5) return { breakMinutes: 15, paidBreakMinutes: 0 }
    // Korte invalshift: te kort voor pauze
    return { breakMinutes: 0, paidBreakMinutes: 0 }
  }
  if (shiftType === 'route') {
    if (grossHours > 10) return { breakMinutes: 45, paidBreakMinutes: 15 }
    if (grossHours > 5.5) return { breakMinutes: 30, paidBreakMinutes: 0 }
    return { breakMinutes: 0, paidBreakMinutes: 0 }
  }
  if (shiftType === 'kantoor') {
    // Volledige dag > 5,5u: 30 min. Korte (ochtend)shift, bv. 08:45-13:00: 15 min.
    if (grossHours > 5.5) return { breakMinutes: 30, paidBreakMinutes: 0 }
    return { breakMinutes: 15, paidBreakMinutes: 0 }
  }
  // algemeen: alleen wettelijk minimum
  if (grossHours > 5.5) return { breakMinutes: 30, paidBreakMinutes: 0 }
  return { breakMinutes: 0, paidBreakMinutes: 0 }
}

export function resolveShiftType(params: {
  scheduleShiftType?: string | null
  staffTeam?: string | null
}): { type: ShiftType; source: 'schedule' | 'team_fallback' } {
  const sched = params.scheduleShiftType
  if (sched === 'wasserij' || sched === 'route' || sched === 'kantoor') {
    return { type: sched, source: 'schedule' }
  }
  const team = params.staffTeam
  if (team === 'wasserij' || team === 'route' || team === 'kantoor') {
    return { type: team, source: 'team_fallback' }
  }
  return { type: 'algemeen', source: 'team_fallback' }
}
